import React from 'react';
import { Wallet } from 'lucide-react';
import { DEST_COLORS } from './constants';
import { fmt } from './utils';

// ═══════════════ SANKEY CHART ═══════════════
// Uses dangerouslySetInnerHTML to bypass platform wrapper injection inside SVG.
const SankeyChart = ({ revenues, fixedCharges, variableCharges, envelopes }) => {
  const totalRev = revenues.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  if (totalRev <= 0) return (
    <div className="flex flex-col items-center justify-center py-12 text-cap-text-muted" data-testid="sankey-empty">
      <Wallet className="w-10 h-10 mb-3 opacity-40" />
      <p className="text-sm">Ajoutez des revenus pour voir le diagramme Sankey</p>
    </div>
  );

  const srcs = revenues.map(r => ({ name: r.name, val: parseFloat(r.amount) || 0 })).filter(r => r.val > 0);
  const dests = [];
  fixedCharges.forEach(c => { const v = parseFloat(c.amount) || 0; if (v > 0) dests.push({ name: c.name, val: v, color: DEST_COLORS.fixed }); });
  variableCharges.forEach(c => { const v = parseFloat(c.amount) || 0; if (v > 0) dests.push({ name: c.name, val: v, color: DEST_COLORS.variable }); });
  envelopes.forEach(e => { const v = parseFloat(e.amount) || 0; if (v > 0) dests.push({ name: e.name, val: v, color: DEST_COLORS[e.type] || DEST_COLORS.provision }); });
  const totalAlloc = dests.reduce((s, d) => s + d.val, 0);
  const unalloc = totalRev - totalAlloc;
  if (unalloc > 0.5) dests.push({ name: 'Non alloué', val: unalloc, color: '#475569' });
  const totalDest = dests.reduce((s, d) => s + d.val, 0) || 1;

  // ─── Layout: explicit margins (top:20, right:180, bottom:20, left:20) ───
  const W = 1000;                         // viewBox base width (stretched to 100% via preserveAspectRatio="none")
  const nW = 20;                          // nodeWidth = 20px
  const nodePadding = 12;                 // nodePadding = 12px
  const marginTop = 20, marginRight = 180, marginBottom = 20, marginLeft = 20;
  const lx = marginLeft;                  // source rect x = 20
  const rx = W - marginRight - nW;        // dest rect x = 800
  const cx = Math.round((lx + nW + rx) / 2 - nW / 2); // budget rect centered between src right and dest left → 410
  const minH = 36;
  const maxN = Math.max(srcs.length, dests.length, 2);
  const containerH = Math.max(300, maxN * 60);
  const contentH = containerH;            // viewBox H matches container so layout fills exactly
  const dH = contentH - marginTop - marginBottom;

  const place = (items, total) => {
    if (!items.length) return [];
    const gapT = nodePadding * (items.length - 1);
    const avail = dH - gapT;
    let heights = items.map(it => Math.max(minH, (it.val / total) * avail));
    const sumH = heights.reduce((s, h) => s + h, 0);
    if (sumH > avail) { const sc = avail / sumH; heights = heights.map(h => Math.max(20, h * sc)); }
    let y = marginTop;
    return items.map((it, i) => { const r = { ...it, y, h: heights[i] }; y += heights[i] + nodePadding; return r; });
  };

  const lN = place(srcs, totalRev);
  const rN = place(dests, totalDest);

  const bez = (x1, y1, h1, x2, y2, h2) => {
    const m = (x1 + x2) / 2;
    return `M${x1},${y1} C${m},${y1} ${m},${y2} ${x2},${y2} L${x2},${y2 + h2} C${m},${y2 + h2} ${m},${y1 + h1} ${x1},${y1 + h1} Z`;
  };

  let lPort = marginTop;
  const lRibbons = lN.map(n => { const ch = (n.val / totalRev) * dH; const r = { sx: lx + nW, sy: n.y, sh: n.h, tx: cx, ty: lPort, th: ch }; lPort += ch; return r; });
  let rPort = marginTop;
  const rRibbons = rN.map(n => { const ch = (n.val / totalDest) * dH; const r = { sx: cx + nW, sy: rPort, sh: ch, tx: rx, ty: n.y, th: n.h, color: n.color }; rPort += ch; return r; });

  const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Build SVG content as raw string to avoid platform wrapper injection inside SVG
  let svg = '<defs>';
  lRibbons.forEach((_, i) => { svg += `<linearGradient id="lgr${i}" x1="0" x2="1"><stop offset="0%" stop-color="#00e5a0" stop-opacity="0.4"/><stop offset="100%" stop-color="#00e5a0" stop-opacity="0.12"/></linearGradient>`; });
  rRibbons.forEach((r, i) => { svg += `<linearGradient id="rgr${i}" x1="0" x2="1"><stop offset="0%" stop-color="${r.color}" stop-opacity="0.12"/><stop offset="100%" stop-color="${r.color}" stop-opacity="0.4"/></linearGradient>`; });
  svg += '</defs>';

  lRibbons.forEach((r, i) => { svg += `<path d="${bez(r.sx, r.sy, r.sh, r.tx, r.ty, r.th)}" fill="url(#lgr${i})"/>`; });
  rRibbons.forEach((r, i) => { svg += `<path d="${bez(r.sx, r.sy, r.sh, r.tx, r.ty, r.th)}" fill="url(#rgr${i})"/>`; });

  // Source nodes (label above rect for clarity since rect is very slim)
  lN.forEach(n => {
    const cy = n.y + n.h / 2;
    svg += `<g><rect x="${lx}" y="${n.y}" width="${nW}" height="${n.h}" rx="4" fill="#00e5a0"/>`;
    svg += `<text x="${lx + nW + 8}" y="${cy - 4}" text-anchor="start" dominant-baseline="middle" fill="white" font-size="13" font-weight="700">${esc(n.name)}</text>`;
    svg += `<text x="${lx + nW + 8}" y="${cy + 13}" text-anchor="start" dominant-baseline="middle" fill="#00e5a0" font-size="12" font-weight="600">${esc(fmt(n.val))}</text>`;
    svg += '</g>';
  });

  // Center "Budget" node
  svg += `<rect x="${cx}" y="${marginTop}" width="${nW}" height="${dH}" rx="6" fill="#151821" stroke="rgba(0,229,160,0.35)" stroke-width="1.5"/>`;
  svg += `<text x="${cx + nW / 2}" y="${contentH / 2 - 6}" text-anchor="middle" fill="white" font-size="14" font-weight="700">Budget</text>`;
  svg += `<text x="${cx + nW / 2}" y="${contentH / 2 + 12}" text-anchor="middle" fill="#00e5a0" font-size="12" font-weight="600">${esc(fmt(totalRev))}</text>`;

  // Destination nodes — labels in the 180px right margin
  rN.forEach(n => {
    const cy = n.y + n.h / 2;
    svg += `<g><rect x="${rx}" y="${n.y}" width="${nW}" height="${n.h}" rx="4" fill="${n.color}"/>`;
    svg += `<text x="${rx + nW + 10}" y="${cy - 4}" text-anchor="start" dominant-baseline="middle" fill="white" font-size="13" font-weight="700">${esc(n.name)}</text>`;
    svg += `<text x="${rx + nW + 10}" y="${cy + 13}" text-anchor="start" dominant-baseline="middle" fill="${n.color}" font-size="12" font-weight="600">${esc(fmt(n.val))}</text>`;
    svg += '</g>';
  });

  return (
    <div className="w-full" style={{ height: containerH }} data-testid="sankey-container">
      <svg
        viewBox={`0 0 ${W} ${contentH}`}
        width="100%"
        height="100%"
        className="block"
        preserveAspectRatio="none"
        data-testid="sankey-svg"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </div>
  );
};

export default SankeyChart;
